import {
  useState,
  type CSSProperties,
  type ImgHTMLAttributes,
  type ReactNode,
} from "react";
import { type LucideIcon } from "lucide-react";
import { CaireLovingHandsMark } from "@appcaire/ui";
import { isRetiredMedia } from "@/config/retiredMedia";

/**
 * Transparent stand-in for a missing capture, showing only the Caire mark.
 */
export function MediaFallback({
  icon: Icon,
  label,
  className = "",
  width,
  height,
  style,
}: {
  icon?: LucideIcon;
  label?: string;
  className?: string;
  width?: number;
  height?: number;
  style?: CSSProperties;
}) {
  const fallbackStyle = {
    ...style,
    aspectRatio: width && height ? `${width} / ${height}` : style?.aspectRatio,
  };
  return (
    <span
      role="img"
      aria-label={label}
      className={`flex w-full items-center justify-center ${className}`}
      style={fallbackStyle}
    >
      {Icon ? (
        <Icon aria-hidden="true" className="h-8 w-8 text-domain-society-ink" />
      ) : (
        <CaireLovingHandsMark className="h-8 w-8 text-domain-society-ink" />
      )}
    </span>
  );
}

/**
 * A product screenshot that falls back to {@link MediaFallback} when the
 * file is retired or fails to load.
 */
export function ProductShot({
  src,
  alt,
  width,
  height,
  className = "",
  loading = "lazy",
  icon,
  style,
  pictureSources,
  pictureClassName,
}: {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  loading?: ImgHTMLAttributes<HTMLImageElement>["loading"];
  icon?: LucideIcon;
  style?: CSSProperties;
  pictureSources?: ReactNode;
  pictureClassName?: string;
}) {
  const [failedSource, setFailedSource] = useState<string | null>(null);
  if (!src || isRetiredMedia(src) || failedSource === src) {
    return (
      <MediaFallback
        icon={icon}
        label={alt}
        className={`${pictureClassName ?? ""} ${className ?? ""}`}
        width={width}
        height={height}
        style={style}
      />
    );
  }
  const loadedImage = (
    <img
      key={src}
      ref={(element) => {
        // An image can fail before React hydrates its onError listener.
        if (element?.complete && element.naturalWidth === 0) {
          setFailedSource(src);
        }
      }}
      src={src}
      alt={alt}
      width={width}
      height={height}
      className={className}
      style={style}
      loading={loading}
      decoding="async"
      onError={() => setFailedSource(src)}
    />
  );
  return pictureSources ? (
    <picture className={pictureClassName}>
      {pictureSources}
      {loadedImage}
    </picture>
  ) : (
    loadedImage
  );
}
